/**
 * Rule-based daily planner.
 * Arranges tasks into time slots based on priority, effort, and deadline.
 */

const PRIORITY_WEIGHT = { 4: 1000, 3: 100, 2: 10, 1: 1 };

const parseTime = (str) => {
    if (!str) return { h: 9, m: 0 };
    const parts = String(str).split(':');
    return { h: Number(parts[0]) || 0, m: Number(parts[1]) || 0 };
};

const minutesFromStart = ({ h, m }, start) => (h - start.h) * 60 + (m - start.m);

const daysUntil = (dateStr) => {
    if (!dateStr) return 999;
    try {
        const target = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        target.setHours(0, 0, 0, 0);
        return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    } catch {
        return 999;
    }
};

/**
 * Score a task — higher score = schedule earlier.
 * Factors:
 *  - Priority (dominant)
 *  - Deadline proximity
 *  - Shorter effort gets preference when priorities are tied (quick wins)
 */
const scoreTask = (task) => {
    const priority = Number(task.priority) || 1;
    const effortMin = Number(task.estimated || task.estimated_effort) || 60;
    const endDate = task.end_date || task.endDate;
    const dLeft = daysUntil(endDate);

    let score = PRIORITY_WEIGHT[priority] || 1;
    // Deadline pressure: overdue or today → boost
    if (dLeft <= 0) score += 500;
    else if (dLeft === 1) score += 50;
    else if (dLeft <= 3) score += 10;
    // Short tasks ranked slightly higher (quick wins)
    if (effortMin <= 30) score += 2;
    return score;
};

/**
 * Build a schedule for today.
 * @param {Array} tasks       Task objects with priority, estimated, end_date, id, title
 * @param {string} startHour  "HH:mm:ss" or "HH:mm"  (default 09:00)
 * @param {string} endHour    "HH:mm:ss" or "HH:mm"  (default 18:00)
 * @param {number} breakMin   Break between tasks in minutes (default 0)
 * @returns {{ schedule: Array<{ task, startTime, endTime, startOffset, durationMin }>, overflow: Array }}
 */
export const planDay = (tasks, startHour = '09:00', endHour = '18:00', breakMin = 0) => {
    const start = parseTime(startHour);
    const end = parseTime(endHour);
    const totalMinutes = (end.h - start.h) * 60 + (end.m - start.m);

    if (totalMinutes <= 0 || !Array.isArray(tasks) || tasks.length === 0) {
        return { schedule: [], overflow: tasks || [] };
    }

    // Only consider actionable tasks (not done, not archived)
    const candidates = tasks
        .filter((t) => {
            const acc = Number(t.accomplished) || 0;
            return acc < 100 && !t.archived;
        })
        .map((t) => ({ ...t, _score: scoreTask(t) }))
        .sort((a, b) => b._score - a._score);

    const schedule = [];
    const overflow = [];
    let cursor = 0; // minutes from day start

    for (const task of candidates) {
        const duration = Math.max(15, Number(task.estimated || task.estimated_effort) || 60);
        if (cursor + duration > totalMinutes) {
            overflow.push(task);
            continue;
        }
        const startOffset = cursor;
        const taskStart = {
            h: start.h + Math.floor(startOffset / 60),
            m: (start.m + startOffset) % 60
        };
        const endOffset = cursor + duration;
        const taskEnd = {
            h: start.h + Math.floor(endOffset / 60),
            m: (start.m + endOffset) % 60
        };
        schedule.push({
            task,
            startTime: `${String(taskStart.h).padStart(2, '0')}:${String(taskStart.m).padStart(2, '0')}`,
            endTime: `${String(taskEnd.h).padStart(2, '0')}:${String(taskEnd.m).padStart(2, '0')}`,
            startOffset,
            durationMin: duration
        });
        cursor = endOffset + breakMin;
    }

    return { schedule, overflow };
};
